import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import moment from 'moment-timezone'
import DayPicker from 'react-day-picker'
import MomentLocaleUtils from 'react-day-picker/moment'

import useConfig from '../../hooks/useConfig'
import { themeFontSize, themeFontWeight, themeColor } from '../../util/themeLenses'
import { DateConstants, formatDate } from '@elparking/utils'
import leftArrow from './images/left-arrow.svg'
import rightArrow from './images/right-arrow.svg'

const MONTH_LONG_FORMAT = DateConstants && DateConstants.MONTH_LONG_FORMAT
const DAY_OF_MONTH_NUMERIC_FORMAT = DateConstants && DateConstants.DAY_OF_MONTH_NUMERIC_FORMAT
const DATE_FORMAT = DateConstants && DateConstants.DATE_FORMAT

export const StyledDayPicker = styled(DayPicker)`
    font-size: 1rem;
    color: ${themeColor('darkMain')};
    font-weight: ${themeFontWeight('regular')};
    display: flex;
    align-items: center;

    .DayPickerInput {
      display: inline-block;
    }

    .DayPickerInput-OverlayWrapper {
        position: relative;
    }

    .DayPickerInput-Overlay {
        position: absolute;
        left: 0;
        z-index: 1;
        background: white;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
    }

    .DayPicker-Months {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
    }
    .DayPicker-Month {
        width: 100%;
        margin-left: 0;
        margin-right: 0;
        display: table;
        margin: 0 1em;
        margin-top: 1em;
        border-spacing: 0;
        border-collapse: collapse;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }

    .DayPicker-wrapper {
        outline: none;
        width: 100%;
        position: relative;
        flex-direction: row;
        padding-bottom: 1em;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }

    .DayPicker-Caption {
        display: table-caption;
        padding: 0 0.5em;
        margin-bottom: 1em;
        font-weight: ${themeFontWeight('medium')};
        text-align: center;
        font-size: ${themeFontSize('xLarge')};
        line-height: 2.5em;
    }

    .DayPicker-Caption > div {
        font-weight: 500;
        font-size: 1.15em;
    }

    .DayPicker-NavButton {
        position: absolute;
        top: 1em;
        right: 1.5em;
        left: auto;
        display: inline-block;
        margin-top: 2px;
        background-position: center;
        background-size: 50%;
        background-repeat: no-repeat;
        color: #8B9898;
        cursor: pointer;
        width: 2.5em;
        height: 2.5em;
        outline: none;
    }

    .DayPicker-NavButton:hover {
        opacity: 0.8;
    }

    .DayPicker-NavButton--prev {
        margin-right: 1.5em;
        left: 0;
        background-image: url('${(props) => props.leftArrowImage || leftArrow}');
    }

    .DayPicker-NavButton--next {
        background-image: url('${(props) => props.rightArrowImage || rightArrow}');
        right: 0;
    }

    .DayPicker-NavButton--interactionDisabled {
        display: none;
    }

    .DayPicker-Weekdays {
        display: table-header-group;
        margin-top: 1em;
    }

    .DayPicker-Weekday {
        display: table-cell;
        padding: 0.5em;
        color: #8B9898;
        text-align: center;
        font-size: 0.875em;
    }

    .DayPicker-WeekdaysRow,
    .DayPicker-Week {
        display: flex;
        justify-content: space-between;
        width: 100%;
    }
    .DayPicker-Body {
        display: table-row-group;
    }

    .DayPicker-Week {
        display: flex;
        -webkit-box-pack: justify;
        justify-content: space-between;
        width: 100%;
    }

    .DayPicker-WeekNumber {
        display: table-cell;
        padding: 0.5em;
        min-width: 1em;
        border-right: 1px solid #EAECEC;
        color: #8B9898;
        vertical-align: middle;
        text-align: right;
        font-size: 0.75em;
        cursor: pointer;
    }

    .DayPicker-Weekday abbr[title] {
        border-bottom: none;
        text-decoration: none;
    }

    .DayPicker-Day {
        display: table-cell;
        padding: 0.5em;
        border-radius: 50%;
        vertical-align: middle;
        text-align: center;
        cursor: pointer;
    }

    .DayPicker-Day,
    .DayPicker-Weekday {
        display: block;
        width: 2.5em;
        height: 2.5em;
        line-height: 2.5em;
        padding: 0;
        margin: 0;
        margin-bottom: 0.5em;
    }

    .DayPicker-Weekday {
        font-weight: ${themeFontWeight('book')};
        color: ${themeColor('gray')};
    }

    .DayPicker-Day {
        outline: none;
        text-align: center;
        border-radius: 50%;

        color: ${themeColor('gray')};
        border: 1px solid ${themeColor('main')};
    }
    .DayPicker-Footer {
        padding-top: 0.5em;
    }

    .DayPicker-TodayButton {
        border: none;
        background-color: transparent;
        background-image: none;
        box-shadow: none;
        color: #4A90E2;
        font-size: 0.875em;
        cursor: pointer;
    }

    .DayPicker--interactionDisabled .DayPicker-Day {
        cursor: default;
    }

    &:not(.DayPicker--interactionDisabled) .DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--selected):not(.DayPicker-Day--outside):hover,
    &:not(.DayPicker--interactionDisabled) .DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--selected):hover {
        background-color: ${themeColor('main')};
        color: ${themeColor('white')};
    }

    .DayPicker-Day--inactive,
    .DayPicker-Day--disabled {
        color: ${themeColor('gray20')};
        background-color: inherit;
    }

    .DayPicker-Day--inactive {
        position: relative;
        color: ${themeColor('gray20')};
    }

    .DayPicker-Day--disabled {
        color: #DCE0E0;
        cursor: default;
        border: none;
        pointer-events: none;
        position: relative;

        &:after{
            content: '';
            position :absolute;
            left: 2em;
            top: 0;
            border-top: 1px solid ${themeColor('gray20')};
            width: 100%;
            transform: rotate(120deg);
            transform-origin: 0% 0%;
        }
    }

    .DayPicker-Day--outside {
        color: #8B9898;
        cursor: default;
    }

    .DayPicker-Day--today {
        color: #D0021B;
        font-weight: 700;
        background: ${themeColor('lightGray20')};
        font-weight: ${themeFontWeight('medium')};
    }

    .DayPicker-Day--today.DayPicker-Day--outside {
        font-weight: ${themeFontWeight('regular')};
    }

    .DayPicker-Day--sunday {
        background-color: #F7F8F8;
    }

    .DayPicker-Day--sunday:not(.DayPicker-Day--today) {
        color: #DCE0E0;
    }

    .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside) {
        position: relative;
    }

    .DayPicker:not(.DayPicker--interactionDisabled)
      .DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--selected):not(.DayPicker-Day--outside):hover {
      background-color: #F0F8FF;
    }

    .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside):hover,
    .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside),
    .DayPicker-Day--selected {
        color: ${themeColor('white')};
        background: ${themeColor('main')};
    }
`

const formatMonthTitle = (date, locale = 'es') => moment(date).locale(locale).format(MONTH_LONG_FORMAT)

export const DayItem = ({ day }) => (
    <span data-test={`calendar-day-${formatDate(day, DATE_FORMAT)}`}>
        {formatDate(day, DAY_OF_MONTH_NUMERIC_FORMAT)}
    </span>
)

DayItem.propTypes = {
    day: PropTypes.instanceOf(Date),
}

const localeUtils = {
    ...MomentLocaleUtils,
    formatMonthTitle,
}

const Days = ({
    selectedDay,
    onChange,
    ...props
}) => {
    const country = useConfig('country')
    const flavor = useConfig('flavor')
    return (<StyledDayPicker
      locale={moment.locale()}
      localeUtils={localeUtils}
      formatMonthTitle={formatMonthTitle}
      onDayClick={onChange}
      selectedDays={selectedDay}
      showOutsideDays
      renderDay={(day) => <DayItem day={day} />}
      country={country}
      flavor={flavor}
      {...props}
    />)
}

Days.propTypes = {
    onChange: PropTypes.func,
    selectedDay: PropTypes.instanceOf(Date),
}

Days.defaultProps = {
    onChange: () => {},
}

export default Days
